import { create } from 'zustand';
import { runtimeConfig } from '@/core/runtimeConfig';
import type { Principal } from '@/models/principal';
import { ApiError } from '@/models/apiError';
import type { ApiErrorBody } from '@/models/apiError';
import { parseOrganizationState } from '@/models/organizationState';

/**
 * App token exchange (E01 §5, Option B). After Entra/MSAL login, the SPA exchanges the Entra
 * identity token ONCE for an app-signed access/refresh token pair carrying the actor context
 * resolved from the App-DB; roles/organization for the UI come from the returned {@link Principal},
 * not Entra ID-token claims. Tokens are kept in memory only (never persisted).
 *
 * The exchange/refresh calls use a plain `fetch`, NOT the openapi-fetch client — that client's
 * auth middleware would call back into the token provider, creating an infinite loop. Both calls
 * pass `credentials: 'include'` so the HttpOnly refresh cookie is sent/received by the browser.
 */

/**
 * Shape of `POST /auth/token` and `/auth/refresh` responses. Mirrors the generated
 * `components['schemas']['TokenResponse']` (see `generated/api/schema.d.ts`); kept as a hand
 * interface (not aliased) because `principal` is typed as the app-level {@link Principal}, whose
 * `readonly` fields are the FE's own guard/context contract, not the wire DTO. There is
 * deliberately no `refreshToken` field — that proof travels only as an HttpOnly cookie.
 */
interface TokenResponse {
  readonly accessToken: string;
  readonly expiresIn: number;
  readonly principal: Principal;
}

/**
 * Wire shape of `principal` before `organizationState` validation — every other field mirrors
 * {@link Principal} as-is, but `organizationState` arrives as a plain string that is not yet
 * known to be a member of the closed `OrganizationState` union.
 */
interface RawPrincipal
  extends Omit<Principal, 'organizationState' | 'selectableOrganizations' | 'partnerLabRejected'> {
  readonly organizationState: string;
  readonly partnerLabRejected?: boolean;
  readonly selectableOrganizations?: Principal['selectableOrganizations'];
}

/** Wire shape of `POST /auth/token` / `/auth/refresh` responses before principal validation. */
interface RawTokenResponse {
  readonly accessToken: string;
  readonly expiresIn: number;
  readonly principal: RawPrincipal;
}

/**
 * Builds a validated {@link Principal} from the raw response payload, parsing
 * `organizationState` fail-closed via {@link parseOrganizationState} instead of blindly trusting
 * the wire value. An unrecognized value resolves to `null`, never `'ACTIVE'`, so
 * `useCanCreateOrders` and the onboarding guard stay locked for any organization state this build
 * does not recognize.
 */
function toPrincipal(raw: RawPrincipal): Principal {
  return {
    ...raw,
    organizationState: parseOrganizationState(raw.organizationState),
    partnerLabRejected: raw.partnerLabRejected === true,
    selectableOrganizations: raw.selectableOrganizations ?? [],
  };
}

interface AppTokenState {
  accessToken: string | null;
  /** Epoch ms at which the access token expires (minus a safety skew). */
  expiresAt: number | null;
  principal: Principal | null;
  setFromResponse: (res: TokenResponse) => void;
  clear: () => void;
}

const EXPIRY_SKEW_MS = 30_000;

const useAppTokenStore = create<AppTokenState>()((set) => ({
  accessToken: null,
  expiresAt: null,
  principal: null,
  setFromResponse: (res) =>
    set({
      accessToken: res.accessToken,
      expiresAt: Date.now() + res.expiresIn * 1000,
      principal: res.principal,
    }),
  clear: () => set({ accessToken: null, expiresAt: null, principal: null }),
}));

/** Reactive hook for the resolved principal (organization/role source for guards & tenant context). */
export function usePrincipal(): Principal | null {
  return useAppTokenStore((s) => s.principal);
}

/** Non-reactive snapshot of the principal. */
export function getPrincipal(): Principal | null {
  return useAppTokenStore.getState().principal;
}

/** Clears all app-token state (call on logout). */
export function clearAppToken(): void {
  useAppTokenStore.getState().clear();
}

function tokenUrl(path: string): string {
  return `${runtimeConfig.apiBaseUrl}${path}`;
}

/**
 * Exchanges an Entra **ID token** (audience = `entraClientId`, not the native-auth access token
 * scoped to Graph) for an app token set. `rememberMe` selects the refresh-cookie mode on the
 * backend (short-lived session cookie vs. persistent sliding-window cookie) and is forwarded
 * as-is in the request body; the resulting HttpOnly cookie itself is never read here —
 * `credentials: 'include'` is what makes the browser store/send it. Stores the access token and
 * the resolved {@link Principal}, whose `onboardingRequired` and optional `userDisplayName` flow
 * straight from the generated `Principal` schema, and returns the access token. Throws on non-2xx.
 *
 * `rememberMe` defaults to `false` (short-lived session cookie) so existing call sites keep
 * compiling until work-item 09 (`useNativeLogin`) wires the login form's actual choice through.
 */
export async function exchange(entraIdToken: string, rememberMe = false): Promise<string> {
  const response = await fetch(tokenUrl('/auth/token'), {
    method: 'POST',
    credentials: 'include',
    headers: { Authorization: `Bearer ${entraIdToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ rememberMe }),
  });
  if (!response.ok) {
    throw new Error(`Token exchange failed: ${response.status}`);
  }
  const raw = (await response.json()) as RawTokenResponse;
  const body: TokenResponse = { ...raw, principal: toPrincipal(raw.principal) };
  useAppTokenStore.getState().setFromResponse(body);
  return body.accessToken;
}

/**
 * Attempts a refresh via the HttpOnly refresh cookie — no body, `credentials: 'include'` so the
 * browser attaches the cookie automatically. Returns the new access token, or null if the
 * refresh fails (missing/expired/invalid cookie), clearing any stale in-memory state so the
 * caller falls back to a fresh Entra-token exchange.
 */
async function refresh(): Promise<string | null> {
  const response = await fetch(tokenUrl('/auth/refresh'), {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    useAppTokenStore.getState().clear();
    return null;
  }
  const raw = (await response.json()) as RawTokenResponse;
  const body: TokenResponse = { ...raw, principal: toPrincipal(raw.principal) };
  useAppTokenStore.getState().setFromResponse(body);
  return body.accessToken;
}

/**
 * Returns a valid app access token, refreshing if it is expired. Returns null if no token is
 * available and no refresh is possible — the caller (token provider) then triggers a fresh
 * exchange from the Entra token.
 */
export async function getAppAccessToken(): Promise<string | null> {
  const { accessToken, expiresAt } = useAppTokenStore.getState();
  if (accessToken !== null && expiresAt !== null && Date.now() < expiresAt - EXPIRY_SKEW_MS) {
    return accessToken;
  }
  return refresh();
}

/** True if an app token set is currently held (after a successful exchange). */
export function hasAppToken(): boolean {
  return useAppTokenStore.getState().accessToken !== null;
}

/**
 * Forces a principal re-resolution via the cookie-based `/auth/refresh` call, for flows that
 * change server-side actor state (e.g. partner-lab selection) and need the in-memory
 * {@link Principal} to reflect that change immediately — unlike {@link getAppAccessToken}, this
 * does not check whether the current access token is still valid; it always calls the backend.
 * Returns the refreshed principal, or null if the refresh cookie is missing/expired (session
 * ended), in which case the in-memory app-token state has already been cleared.
 */
export async function refreshPrincipal(): Promise<Principal | null> {
  await refresh();
  return getPrincipal();
}

/**
 * Switches the active laboratory; plain `fetch` like {@link refresh} because the backend also
 * replaces the refresh cookie. Throws an {@link ApiError} carrying the backend `code` on non-2xx.
 */
export async function switchActiveOrganization(organizationId: string): Promise<Principal> {
  const accessToken = await getAppAccessToken();
  const response = await fetch(tokenUrl('/auth/active-organization'), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken !== null && { Authorization: `Bearer ${accessToken}` }),
    },
    body: JSON.stringify({ organizationId }),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({
      error: response.statusText,
      code: 'UNKNOWN',
      timestamp: new Date().toISOString(),
      path: '/auth/active-organization',
    }))) as ApiErrorBody;
    throw new ApiError(response.status, body, response.headers.get('X-Correlation-Id'));
  }
  const raw = (await response.json()) as RawTokenResponse;
  const body: TokenResponse = { ...raw, principal: toPrincipal(raw.principal) };
  useAppTokenStore.getState().setFromResponse(body);
  return body.principal;
}
